import { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import clsx from 'clsx';
import { ExportRepaymentScheduleToCSVButton } from 'components/ExportRepaymentScheduleToCSVButton/ExportRepaymentScheduleToCSVButton';
import { RepaymentScheduleTable } from 'components/RepaymentScheduleTable/RepaymentScheduleTable';
import { useLocale } from 'hooks/useLocale';
import { useNumberFormat } from 'hooks/useNumberFormat';
import { useGetRepaymentSimulator } from 'lendingApi/lendingApiHooks';
import { TCalculatorProducts, TDurationDetails } from 'types';
import {
  MAX_CREDIT_FACILITY_DURATION,
  MIN_DE_AMOUNT,
  MIN_DE_INTEREST_RATE,
  MIN_DURATION,
  MIN_UK_AMOUNT,
  MIN_UK_INTEREST_RATE,
  MIN_WITHDRAWAL_FEE,
} from './filterConfigs.helper';
import { InterestRateFilter } from './InterestRateFilter/InterestRateFilter';
import { LoanAmountFilter } from './LoanAmountFilter/LoanAmountFilter';
import { OverpaymentOptions } from './OverpaymentOptions/OverpaymentOptions';
import { ProductFilter } from './ProductFilter/ProductFilter';
import { RepaymentScheduleLoader } from './RepaymentScheduleLoader';
import styles from './StandAloneCalculator.module.css';
import { WithdrawalFeeFilter } from './WithdrawalFeeFilter/WithdrawalFeeFilter';

export const StandAloneCalculator = () => {
  const { isUK, isDE } = useLocale();
  const { formatCurrency } = useNumberFormat();

  const [showOverpaymentSection, setShowOverpaymentSection] = useState(false);
  const [overpaymentAmount, setOverpaymentAmount] = useState<string>();
  const [amount, setAmount] = useState(isDE ? MIN_DE_AMOUNT : MIN_UK_AMOUNT);
  const [withdrawalFee, setWithdrawalFee] = useState(MIN_WITHDRAWAL_FEE);
  const [duration, setDuration] = useState(MIN_DURATION);
  const [maxTermLength, setMaxTermLength] = useState(
    Number(MAX_CREDIT_FACILITY_DURATION),
  );

  const [product, setProduct] =
    useState<TCalculatorProducts>('credit_facility');
  const [interestRate, setInterestRate] = useState(
    isDE ? MIN_DE_INTEREST_RATE : MIN_UK_INTEREST_RATE,
  );

  const [earlyRepaymentStartDate, setEarlyRepaymentStartDate] = useState<
    string | null
  >(null);

  const [earlyRepaymentEndDate, setEarlyRepaymentEndDate] = useState<
    string | null
  >(null);

  const [queryKey, setQueryKey] = useState<
    (string | number | null | undefined)[]
  >([]);

  const { data: repaymentSchedule, isFetching } = useGetRepaymentSimulator({
    isDE,
    product,
    interestRate,
    amount,
    duration,
    maxTermLength,
    earlyRepaymentStartDate,
    earlyRepaymentEndDate,
    overpaymentAmount,
    withdrawalFee,
    queryKey,
  });

  useEffect(() => {
    const debouncedSetQueryKey = setTimeout(() => {
      setQueryKey([
        product,
        amount,
        interestRate,
        maxTermLength,
        earlyRepaymentStartDate,
        earlyRepaymentEndDate,
        overpaymentAmount,
        withdrawalFee,
      ]);
    }, 350);

    return () => {
      clearTimeout(debouncedSetQueryKey);
    };
  }, [
    amount,
    interestRate,
    product,
    maxTermLength,
    earlyRepaymentStartDate,
    earlyRepaymentEndDate,
    overpaymentAmount,
    withdrawalFee,
  ]);

  useEffect(() => {
    setAmount(isUK ? MIN_UK_AMOUNT : MIN_DE_AMOUNT);
    setInterestRate(isDE ? MIN_DE_INTEREST_RATE : MIN_UK_INTEREST_RATE);
    setWithdrawalFee(MIN_WITHDRAWAL_FEE);
    setEarlyRepaymentEndDate(null);
    setShowOverpaymentSection(false);
    setOverpaymentAmount(undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product, maxTermLength]);

  const setOverpaymentDurationDates = ({
    startDate,
    endDate,
    duration,
  }: TDurationDetails) => {
    setDuration(duration);
    setEarlyRepaymentStartDate(startDate);
    setEarlyRepaymentEndDate(endDate);
  };

  const setProductAndMaxTerm = (
    newProduct: TCalculatorProducts,
    newMaxTerm: number,
  ) => {
    setProduct(newProduct);
    setMaxTermLength(newMaxTerm);
  };

  const renderSavings = () => {
    const summary = repaymentSchedule?.data.summary;
    const schedule = repaymentSchedule?.data.schedule;
    const length = summary?.total_number_of_repayments;

    if (
      !schedule ||
      !summary?.amount_saved ||
      !length ||
      summary.amount_saved < 0
    ) {
      return null;
    }

    const savedAmount = formatCurrency(summary.amount_saved);

    const lengthText =
      length > 1 ? t`in ${length} months` : t`in ${length} month`;

    return (
      <p className={styles.scheduleSavings}>
        <Trans>
          Save {savedAmount} by paying off the full loan {lengthText}
        </Trans>
      </p>
    );
  };

  const renderRepaymentSchedule = () => {
    if (isFetching || !repaymentSchedule) {
      const numberOfRowsLoading = Number(duration) <= 9 ? Number(duration) : 9;
      return (
        <RepaymentScheduleLoader numberOfRowsLoading={numberOfRowsLoading} />
      );
    }

    return (
      <>
        <RepaymentScheduleTable
          schedule={repaymentSchedule?.data.schedule}
          summary={repaymentSchedule?.data.summary}
        />
        <div className={styles.scheduleFooter}>
          {renderSavings()}
          <p className={styles.scheduleExample}>
            <Trans>
              Representative example: Borrow £10,000 for 12 Months at 49%
              representative APR. Interest rate of 40% p.a. (fixed). Total
              amount repayable is £12,165.
            </Trans>
          </p>
          <p className={styles.scheduleFootnote}>
            <Trans>*Actual rate may vary based on circumstances.</Trans>
          </p>
        </div>
      </>
    );
  };

  return (
    <div className={clsx(styles.container, { [styles.isDE]: isDE })}>
      <div className={styles.filterContainer}>
        <h1 className={styles.heading}>
          <Trans>Repayment calculator</Trans>
        </h1>
        <ProductFilter value={maxTermLength} onChange={setProductAndMaxTerm} />
        <LoanAmountFilter value={amount} onChange={setAmount} />
        <InterestRateFilter
          product={product}
          value={interestRate}
          maxTermLength={maxTermLength}
          onChange={setInterestRate}
        />
        {isUK && (
          <>
            <WithdrawalFeeFilter
              value={withdrawalFee}
              onChange={setWithdrawalFee}
            />
          </>
        )}
        <hr className={styles.seperator} />
        <OverpaymentOptions
          showOverpayment={showOverpaymentSection}
          maxTermLength={maxTermLength}
          overpaymentAmount={overpaymentAmount}
          setShowOverpayment={setShowOverpaymentSection}
          onOverpaymentDurationChange={setOverpaymentDurationDates}
          onOverpaymentChange={setOverpaymentAmount}
        />
      </div>
      <div className={styles.scheduleContainer}>
        <div className={styles.scheduleHeadingWrapper}>
          <h2 className={styles.scheduleHeading}>
            <Trans>Example repayment schedule</Trans>
          </h2>
          {repaymentSchedule && (
            <ExportRepaymentScheduleToCSVButton
              schedule={repaymentSchedule?.data.schedule}
            />
          )}
        </div>

        {renderRepaymentSchedule()}
      </div>
    </div>
  );
};
